<script setup lang="ts">
import type { MaskInputOptions } from 'maska';
import { computed, reactive } from 'vue';
import { mandatory } from '../../composables/validations';
import type { Address } from '../../types/address';

interface Props {
  required?: boolean;
}

const { required = false } = defineProps<Props>();

const modelValue = defineModel<Address>({
  default: () => ({
    street: '',
    houseNumber: '',
    suffix: '',
    zipCode: '',
    city: '',
  }),
});

const maskNumber = reactive<MaskInputOptions>({
  mask: '#####',
  eager: true,
});

const street = computed<string>({
  get: () => modelValue.value.street ?? '',
  set: (value: string) => { modelValue.value = { ...modelValue.value, street: value }; },
});
const houseNumber = computed<string>({
  get: () => modelValue.value.houseNumber ?? '',
  set: (value: string) => { modelValue.value = { ...modelValue.value, houseNumber: value }; },
});
const suffix = computed<string>({
  get: () => modelValue.value.suffix ?? '',
  set: (value: string) => { modelValue.value = { ...modelValue.value, suffix: value }; },
});
const zipCode = computed<string>({
  get: () => modelValue.value.zipCode ?? '',
  set: (value: string) => { modelValue.value = { ...modelValue.value, zipCode: value }; },
});
const city = computed<string>({
  get: () => modelValue.value.city ?? '',
  set: (value: string) => { modelValue.value = { ...modelValue.value, city: value }; },
});

const rulesRequired = computed(() => (required ? mandatory : []));
const rulesZipCode = computed(
  () => (required
    ? [...mandatory, (v: string) => v.length === 5 || 'Die Postleitzahl muss 5 Zahlen beinhalten']
    : []),
);
</script>

<template>
  <div>
    <v-row no-gutters>
      <v-col cols="6" class="pr-2">
        <v-text-field v-model="street" :rules="rulesRequired">
          <template #label>
            <span>Straße<sup v-if="rulesRequired" class="required">*</sup></span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="3" class="px-2">
        <v-text-field v-model="houseNumber" v-maska="maskNumber" :rules="rulesRequired">
          <template #label>
            <span>Hausnummer<sup v-if="rulesRequired" class="required">*</sup></span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="3" class="pl-2">
        <v-text-field v-model="suffix" label="Zusatz" />
      </v-col>
    </v-row>
    <v-row no-gutters>
      <v-col cols="4" class="pr-2">
        <v-text-field v-model="zipCode" v-maska="maskNumber" :rules="rulesZipCode">
          <template #label>
            <span>PLZ<sup v-if="rulesZipCode" class="required">*</sup></span>
          </template>
        </v-text-field>
      </v-col>
      <v-col cols="8" class="pl-2">
        <v-text-field v-model="city" :rules="rulesRequired">
          <template #label>
            <span>Ort<sup v-if="rulesRequired" class="required">*</sup></span>
          </template>
        </v-text-field>
      </v-col>
    </v-row>
  </div>
</template>
